/* External */
import React, {
  useEffect, useState, useRef, useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import bowser from 'bowser';

/* Ott-common */
import { Errors, getPlaybackInfoWithAuthorizerV3 } from '@dtvgo/player-utils';
import { useGetAndSet, useGetSync } from '@dtvgo/react-utils';
import { rtkQueryTags } from '@dtvgo/rtk-query-api';

/* Components */
import Player from '../Player';

/* Other */
import { backendService } from '../../services';
import { api } from '../../state/configureStore';
import { USE_LOW_LATENCY_STREAMS, isBeforeUnloadStopActive } from '../../utils/env';
import { startHeartbeat, stopHeartbeat } from '../../state/player/streamHeartbeat/vrioHeartbeatSlice';
import { checkToken } from '../../services/auth';
import { getUserDetailsToken } from '../../utils';

function VrioPlayer({
  isLive,
  playbackId,
  readyToInit,
  savedProgress,
  asset,
  onReadyToPlay,
  onPlay,
  onPlaying,
  onPause,
  onProgress,
  onEnded,
  onAutoplayFailed,
  onError,
  onAuthorizerError,
  onAuthorizerResponseReceived,
  playbackHeartbeatController,
  streamedTimeCounter,
  streamedContentTimeCounter,
  children,
}) {
  const { msoProvider, userType } = getUserDetailsToken();
  const getAsset = useGetSync(asset);
  const getOnAuthorizerError = useGetSync(onAuthorizerError);
  const getOnAuthorizerResponseReceived = useGetSync(onAuthorizerResponseReceived);
  const dispatch = useDispatch();
  const [sources, setSources] = useState([]);
  const clearRefreshTimeoutRef = useRef(null);
  const prevPlaybackId = useRef();

  const [getIsPlaying, setIsPlaying] = useGetAndSet(false);

  const { playSource, actionOrigin } = useSelector((state) => ({
    playSource: state.player.playSource,
    actionOrigin: state.player.actionOrigin,
  }));

  const handlePlaybackStopped = useCallback(async (playingFalse = true) => {
    if (playingFalse) {
      setIsPlaying(false);
    }
    streamedTimeCounter?.stop();
    streamedContentTimeCounter?.stop();
    if (clearRefreshTimeoutRef.current) clearRefreshTimeoutRef.current();
    await playbackHeartbeatController?.stop().then((data) => {
      dispatch(stopHeartbeat());

      return data;
    });
    streamedTimeCounter?.reset();
    streamedContentTimeCounter?.reset();
  }, [
    dispatch,
    playbackHeartbeatController,
    setIsPlaying,
    streamedTimeCounter,
    streamedContentTimeCounter,
  ]);

  const handleAuthorizerError = useCallback((error) => {
    getOnAuthorizerError()?.(Errors.parseAuthorizerError(error));
  }, [getOnAuthorizerError]);

  const handleError = useCallback(
    (error) => {
      handlePlaybackStopped();
      onError(error);
    },
    [handlePlaybackStopped, onError],
  );

  const handlePlaying = useCallback(() => {
    onPlaying();
    streamedTimeCounter.start();
    streamedContentTimeCounter.start();
    setIsPlaying(true);

    playbackHeartbeatController?.updateActionOrigin(actionOrigin || '');
    playbackHeartbeatController?.updateUserOrigin({
      playSource: playSource || '',
      userLogin: userType || '',
      msoProvider: msoProvider || '',
    });
  }, [
    onPlaying,
    setIsPlaying,
    streamedTimeCounter,
    streamedContentTimeCounter,
    actionOrigin,
    playSource,
    userType,
    msoProvider,
    playbackHeartbeatController,
  ]);

  const handlePause = useCallback(() => {
    onPause();
    streamedTimeCounter.stop();
    streamedContentTimeCounter.stop();
    setIsPlaying(false);
  }, [
    onPause,
    setIsPlaying,
    streamedTimeCounter,
    streamedContentTimeCounter,
  ]);

  const handleProgress = useCallback((progress) => {
    playbackHeartbeatController?.updateProgress(progress);
    onProgress(progress);
  }, [onProgress, playbackHeartbeatController]);

  const handleDurationChange = useCallback((duration) => {
    playbackHeartbeatController?.updateDuration(duration);
  }, [playbackHeartbeatController]);

  const handleBuffering = useCallback(() => {
    streamedTimeCounter.stop();
    streamedContentTimeCounter.stop();
  }, [streamedTimeCounter, streamedContentTimeCounter]);

  const handleBufferingEnded = useCallback(() => {
    if (getIsPlaying()) {
      streamedTimeCounter.start();
      streamedContentTimeCounter.start();
    }
  }, [getIsPlaying, streamedTimeCounter, streamedContentTimeCounter]);

  const handleEnded = useCallback(() => {
    handlePlaybackStopped();
    onEnded();
  }, [handlePlaybackStopped, onEnded]);

  const getAssetAndScheduleForHearbeat = useCallback(() => {
    const currentAsset = getAsset();
    if (!currentAsset) {
      return { asset: undefined, schedule: undefined };
    }
    return {
      asset: {
        vmsId: currentAsset.vmsId,
        vrioAssetId: currentAsset.vrioAssetId,
        duration: currentAsset.duration,
      },
      schedule: {
        guideId: currentAsset.guideId,
        startTime: currentAsset.live?.startTime && new Date(currentAsset.live?.startTime),
        endTime: currentAsset.live?.endTime && new Date(currentAsset.live?.endTime),
      },
    };
  }, [getAsset]);

  useEffect(() => {
    async function initPlayback() {
      const previousActionOrigin = localStorage.getItem('previousActionOrigin');
      const previousPlaySource = localStorage.getItem('previousPlaySource');

      if (previousActionOrigin && previousPlaySource) {
        playbackHeartbeatController?.updateActionOrigin(previousActionOrigin || '');
        playbackHeartbeatController?.updateUserOrigin({
          playSource: previousPlaySource || '',
          userLogin: userType || '',
          msoProvider: msoProvider || '',
        });

        localStorage.removeItem('previousActionOrigin');
        localStorage.removeItem('previousPlaySource');
      }

      await handlePlaybackStopped(false);
      try {
        const {
          sources: newSources,
          clearRefreshTimeout,
          ...authorizerResponse
        } = await getPlaybackInfoWithAuthorizerV3(
          backendService,
          {
            isLive,
            contentId: playbackId,
          },
          {
            onAuthorizerError: handleAuthorizerError,
          },
          checkToken,
        );

        clearRefreshTimeoutRef.current = clearRefreshTimeout;
        const filteredSources = USE_LOW_LATENCY_STREAMS
          ? newSources
          : newSources?.filter(({ isLowLatency }) => !isLowLatency);

        setSources(filteredSources.map((source) => ({
          ...source,
          keySystemsConfig: source.keySystemsConfig ? {
            ...source.keySystemsConfig,
            preferredKeySystems: ['widevine'],
            widevine: {
              ...source.keySystemsConfig.widevine,
              persistentStateRequired: !bowser.linux,
            },
          } : {},
        })));

        getOnAuthorizerResponseReceived()?.(authorizerResponse);
      } catch (error) {
        handleAuthorizerError(error);
        return;
      }

      await playbackHeartbeatController?.start({
        playbackId,
        isLive,
        ...getAssetAndScheduleForHearbeat(),
      });

      dispatch(startHeartbeat());
    }

    if (playbackId && readyToInit && playbackId !== prevPlaybackId.current) {
      prevPlaybackId.current = playbackId;
      initPlayback();
    }
  }, [
    dispatch,
    playbackId,
    prevPlaybackId,
    readyToInit,
    isLive,
    handleAuthorizerError,
    handlePlaybackStopped,
    playbackHeartbeatController,
    getOnAuthorizerResponseReceived,
    getAssetAndScheduleForHearbeat,
    msoProvider,
    userType,
  ]);

  /* When asset changes update the content for heartbeat */
  useEffect(() => {
    const { asset: assetForHeartbeat, schedule } = getAssetAndScheduleForHearbeat();
    playbackHeartbeatController?.setContent({
      asset: assetForHeartbeat,
      schedule,
      isLive,
    });
  }, [
    asset?.guideId,
    asset?.vrioAssetId,
    getAssetAndScheduleForHearbeat,
    isLive,
    playbackHeartbeatController,
  ]);

  /* When component is unmounted invalidates ThinkAnalytics endpoints caché so other components
     can get the latest results */
  useEffect(
    () => () => {
      dispatch(api.util.invalidateTags([rtkQueryTags.thinkAnalytics]));
    },
    [dispatch],
  );

  /* When component is unmounted, stop playback */
  useEffect(() => () => {
    handlePlaybackStopped();
  }, [handlePlaybackStopped]);

  /* When page is unloaded, stop playback */
  useEffect(() => {
    if (!isBeforeUnloadStopActive) {
      const handleUnload = () => {
        handlePlaybackStopped();
      };
      document.addEventListener('unload', handleUnload);
      return () => {
        document.removeEventListener('unload', handleUnload);
      };
    }
    const handleUnload = () => {
      const video = document.querySelector('.dtv-common-c-shaka-player__video');
      if (video) {
        video.pause();
      }
      streamedTimeCounter.stop();
      playbackHeartbeatController?.sendStop();
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [handlePlaybackStopped, playbackHeartbeatController, streamedTimeCounter]);

  return (
    <Player
      isLive={isLive}
      content={asset}
      sources={sources}
      savedProgress={savedProgress}
      onReadyToPlay={onReadyToPlay}
      onPlay={onPlay}
      onPlaying={handlePlaying}
      onPause={handlePause}
      onBuffering={handleBuffering}
      onBufferingEnded={handleBufferingEnded}
      onProgress={handleProgress}
      onEnded={handleEnded}
      onDurationChange={handleDurationChange}
      onAutoplayFailed={onAutoplayFailed}
      onError={handleError}
    >
      {children}
    </Player>
  );
}

VrioPlayer.propTypes = {
  isLive: PropTypes.bool,
  playbackId: PropTypes.string,
  readyToInit: PropTypes.bool,
  savedProgress: PropTypes.number,
  asset: PropTypes.shape({
    guideId: PropTypes.string,
    vmsId: PropTypes.string,
    vrioAssetId: PropTypes.string,
    live: PropTypes.shape({
      startTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
      endTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    }),
  }),
  onReadyToPlay: PropTypes.func,
  onPlay: PropTypes.func,
  onPlaying: PropTypes.func,
  onPause: PropTypes.func,
  onProgress: PropTypes.func,
  onEnded: PropTypes.func,
  onAutoplayFailed: PropTypes.func,
  onError: PropTypes.func,
  onAuthorizerError: PropTypes.func,
  onAuthorizerResponseReceived: PropTypes.func,
  children: PropTypes.node,
};

VrioPlayer.defaultProps = {
  isLive: false,
  playbackId: undefined,
  readyToInit: true,
  savedProgress: undefined,
  asset: undefined,
  onReadyToPlay: () => {},
  onProgress: () => {},
  onEnded: () => {},
  onPlay: () => {},
  onPlaying: () => {},
  onPause: () => {},
  onAutoplayFailed: () => {},
  onError: () => {},
  onAuthorizerError: () => {},
  onAuthorizerResponseReceived: () => {},
  children: null,
};

export default VrioPlayer;
